import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Gauge, ArrowRight } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { checkEligibility } from "../lib/api";
import { useLead } from "../context/LeadContext";

const CIBIL = [
  { v: "750+", l: "Excellent (750+)" },
  { v: "700-749", l: "Good (700–749)" },
  { v: "650-699", l: "Fair (650–699)" },
  { v: "600-649", l: "Low (600–649)" },
  { v: "below-600", l: "Very Low (Below 600)" },
  { v: "no-cibil", l: "No / New CIBIL" },
];

export const EligibilityChecker = () => {
  const [form, setForm] = useState({
    employment: "salaried",
    monthly_income: "",
    loan_amount: "",
    cibil_range: "",
    name: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { openLead } = useLead();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.monthly_income || !form.loan_amount || !form.cibil_range) {
      toast.error("Please fill income, loan amount and CIBIL range");
      return;
    }
    setLoading(true);
    try {
      const res = await checkEligibility({
        ...form,
        monthly_income: Number(form.monthly_income),
        loan_amount: Number(form.loan_amount),
      });
      setResult(res);
    } catch {
      toast.error("Could not check right now. Please contact us directly.");
    } finally {
      setLoading(false);
    }
  };

  const verdictColor = result?.verdict === "High" ? "text-approval" : result?.verdict === "Good" ? "text-gold" : "text-orange-400";

  return (
    <div className="card-dark rounded-2xl p-6 sm:p-8" data-testid="eligibility-checker">
      {!result ? (
        <form onSubmit={submit} className="space-y-5" data-testid="eligibility-checker-form">
          <div>
            <Label className="text-sm text-slate-300 mb-2.5 block">Employment Type</Label>
            <RadioGroup
              value={form.employment}
              onValueChange={(v) => set("employment", v)}
              className="grid grid-cols-3 gap-2"
            >
              {[["salaried", "Salaried"], ["self-employed", "Self Employed"], ["business", "Business"]].map(([v, l]) => (
                <label key={v} className={`cursor-pointer rounded-xl border p-2.5 text-center text-sm transition-colors ${form.employment === v ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-slate-400 hover:border-white/20"}`}>
                  <RadioGroupItem value={v} className="sr-only" data-testid={`eligibility-emp-${v}`} />
                  {l}
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-slate-300 mb-2 block">Monthly Income (₹)</Label>
              <Input
                data-testid="eligibility-income-input"
                type="number" placeholder="e.g. 50000"
                value={form.monthly_income}
                onChange={(e) => set("monthly_income", e.target.value)}
                className="bg-obsidian-900/60 border-white/10 h-11 text-white"
              />
            </div>
            <div>
              <Label className="text-sm text-slate-300 mb-2 block">Required Loan (₹)</Label>
              <Input
                data-testid="eligibility-amount-input"
                type="number" placeholder="e.g. 500000"
                value={form.loan_amount}
                onChange={(e) => set("loan_amount", e.target.value)}
                className="bg-obsidian-900/60 border-white/10 h-11 text-white"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm text-slate-300 mb-2 block">CIBIL / Credit Range</Label>
            <Select value={form.cibil_range} onValueChange={(v) => set("cibil_range", v)}>
              <SelectTrigger data-testid="eligibility-cibil-select" className="bg-obsidian-900/60 border-white/10 h-11 text-white">
                <SelectValue placeholder="Select your credit range" />
              </SelectTrigger>
              <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                {CIBIL.map((c) => (
                  <SelectItem key={c.v} value={c.v} className="focus:bg-gold/20 focus:text-white">{c.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              data-testid="eligibility-name-input"
              placeholder="Your Name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="bg-obsidian-900/60 border-white/10 h-11 text-white"
            />
            <Input
              data-testid="eligibility-phone-input"
              placeholder="Mobile Number"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className="bg-obsidian-900/60 border-white/10 h-11 text-white"
            />
          </div>

          <Button
            type="submit" disabled={loading}
            data-testid="eligibility-submit-button"
            className="w-full gold-gradient text-obsidian-900 font-bold h-12 rounded-full hover:opacity-90"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Check My Eligibility <ArrowRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </form>
      ) : (
        <div className="text-center py-4" data-testid="eligibility-score-result">
          <Gauge className={`w-12 h-12 mx-auto mb-3 ${verdictColor}`} />
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Approval Likelihood</div>
          <div className={`font-mono-fin text-6xl font-extrabold my-2 ${verdictColor}`}>{result.score}%</div>
          <div className={`font-heading text-xl font-bold ${verdictColor}`}>{result.verdict}</div>
          <p className="text-slate-400 text-sm mt-3 max-w-sm mx-auto">{result.message}</p>
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button
              onClick={() => openLead({ source: "eligibility-result", amount: form.loan_amount })}
              className="flex-1 gold-gradient text-obsidian-900 font-bold h-11 rounded-full"
              data-testid="eligibility-proceed-button"
            >
              Proceed with Application
            </Button>
            <Button
              onClick={() => setResult(null)}
              variant="outline"
              className="flex-1 border-white/15 text-slate-200 hover:bg-white/5 h-11 rounded-full"
              data-testid="eligibility-recheck-button"
            >
              Check Again
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-4">*Indicative estimate. Final approval subject to lender policy & documentation.</p>
        </div>
      )}
    </div>
  );
};
